import React, { ChangeEvent, useState } from "react";

import { InfoIcon } from "../Icons";
import { Typo } from "../Typo";
import { TooltipModal } from "./TooltipModal";

type CheckboxProps = {
  className?: string;
  id: string;
  label: React.ReactNode;
  tooltipText?: string;
  checked?: boolean;
  error?: boolean;
  errorMessage?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleToolTipClick?: () => void;
  disabled?: boolean;
};

export const Checkbox: React.FC<CheckboxProps> = ({
  className,
  id,
  label,
  tooltipText,
  checked,
  error,
  errorMessage,
  onChange,
  handleToolTipClick,
  disabled,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`inline-flex items-start gap-2 ${className ?? ""} ${disabled ? "opacity-50" : ""}`}
      >
        <label
          className={`relative flex items-center ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          htmlFor={id}
        >
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className={`peer h-5 w-5 appearance-none rounded border border-gray-300 bg-white transition-all checked:border-slate-800 checked:bg-slate-800 ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${
              error ? "border-2 border-red-500" : ""
            }`}
          />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-white opacity-0 peer-checked:opacity-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              viewBox="0 0 20 20"
              fill="white"
              stroke="currentColor"
              strokeWidth="1"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              ></path>
            </svg>
          </span>
        </label>
        <label
          htmlFor={id}
          className="font-body text-sm leading-5 text-gray-900"
        >
          <Typo element="span" type="labelSM">
            {label}
          </Typo>

          {tooltipText && (
            <>
              &nbsp;
              <InfoIcon
                className="inline cursor-pointer items-center [&_path]:stroke-icon-base"
                desc={`tooltip-${id}`}
                onClick={(e: React.MouseEvent<SVGElement>) => {
                  e.preventDefault();
                  handleToolTipClick && handleToolTipClick();
                  setShowTooltip(true);
                }}
              />
            </>
          )}
        </label>
      </div>
      {error && errorMessage && (
        <Typo element="span" type="bodyXS" textColor="text-text-error">
          {errorMessage}
        </Typo>
      )}
      {tooltipText && (
        <TooltipModal
          tooltipText={tooltipText}
          isOpen={showTooltip}
          onClose={() => setShowTooltip(false)}
        />
      )}
    </div>
  );
};
